import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { api, API_BASE } from "../lib/api";
import BookReader from "../components/BookReader";

const EASE = [0.16, 1, 0.3, 1];

export default function Landing() {
  const [meta, setMeta] = useState(null);
  const [photos, setPhotos] = useState(null);
  const [bookOpen, setBookOpen] = useState(false);
  const [tooSmall, setTooSmall] = useState(false);

  useEffect(() => {
    api
      .get("/book")
      .then((r) => setMeta(r.data.meta))
      .catch(() => setMeta({ title: "The Bible", author: "Trevor Lee-Gervais" }));
    api
      .get("/photos/manifest")
      .then((r) => setPhotos(r.data))
      .catch(() => setPhotos({ status: "none" }));
  }, []);

  useEffect(() => {
    if (!tooSmall) return;
    const t = setTimeout(() => setTooSmall(false), 3000);
    return () => clearTimeout(t);
  }, [tooSmall]);

  const openBook = () => {
    if (!photos || photos.status !== "ready") return;
    if (photos.width > window.screen.width || photos.height > window.screen.height) {
      setTooSmall(true);
      return;
    }
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    }
    setBookOpen(true);
  };

  const closeBook = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    setBookOpen(false);
  };

  if (!meta) return <div className="min-h-screen bg-[#0B0C0E]" />;

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-[#0B0C0E] text-[#F4F1EA]">
      <div className="grain-overlay" />

      <header className="flex justify-center pt-12 sm:pt-16">
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2, ease: EASE }}
          data-testid="home-author"
          className="cursor-default font-sans text-xs uppercase tracking-[0.45em] text-[#F4F1EA] sm:text-sm"
        >
          {meta.author}
        </motion.p>
      </header>

      <main className="flex flex-1 items-center justify-center px-6">
        <h1 data-testid="home-title" className="cursor-default overflow-hidden text-center">
          <motion.span
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.3, delay: 0.45, ease: EASE }}
            className="block font-display text-6xl font-light uppercase leading-none tracking-tight text-[#F4F1EA] sm:text-8xl lg:text-9xl"
          >
            {meta.title}
          </motion.span>
        </h1>
      </main>

      <footer className="flex flex-col items-center justify-center gap-4 pb-16 sm:flex-row sm:gap-6 sm:pb-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
        >
          <button
            type="button"
            onClick={openBook}
            data-testid="home-read-btn"
            className="block rounded-full border border-[#F4F1EA]/60 px-14 py-4 text-center font-sans text-sm uppercase tracking-[0.3em] text-[#F4F1EA] transition-colors hover:border-[#D4A373] hover:text-[#D4A373]"
          >
            Read
          </button>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
        >
          <a
            href={`${API_BASE}/download/app-windows`}
            data-testid="home-download-btn"
            className="block rounded-full border border-[#F4F1EA]/60 px-14 py-4 text-center font-sans text-sm uppercase tracking-[0.3em] text-[#F4F1EA] transition-colors hover:border-[#D4A373] hover:text-[#D4A373]"
          >
            Download
          </a>
        </motion.div>
      </footer>

      <Link
        to="/admin"
        data-testid="home-studio-link"
        className="absolute bottom-5 right-6 font-sans text-[10px] uppercase tracking-[0.3em] text-[#6E6B63] opacity-50 transition-opacity hover:opacity-100"
      >
        Studio
      </Link>

      <AnimatePresence>
        {tooSmall && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            data-testid="screen-too-small-msg"
            className="pointer-events-none fixed inset-0 z-[70] flex items-center justify-center bg-[#0B0C0E]/80 px-6 text-center font-sans text-sm tracking-[0.08em] text-[#A39E93]"
          >
            Your screen is too small.
          </motion.p>
        )}
      </AnimatePresence>

      {bookOpen && photos && photos.status === "ready" && <BookReader manifest={photos} onClose={closeBook} />}
    </div>
  );
}
